import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faLinkedinIn } from '@fortawesome/free-brands-svg-icons';

const FooterSection = styled.footer`
  background-color: #1a1a1a;
  color: #fff;
  padding-top: 80px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  padding-bottom: 60px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterWidget = styled(motion.div)`
  margin-bottom: 30px;
  
  &.with-border {
    border-right: 2px solid #ff6e6e;
    
    @media (max-width: 768px) {
      border-right: none;
    }
  }
`;

const FooterLogo = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
`;

const FooterText = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 25px;
  text-align: left;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 10px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #ff6e6e;
    transform: translateY(-3px);
  }
`;

const WidgetTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 25px;
  position: relative;
  padding-bottom: 10px;
  text-align: left;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 50px;
    height: 2px;
    background-color: #ff6e6e;
  }
`;

const QuickLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const QuickLink = styled.li`
  margin-bottom: 12px;
`;

const Link = styled.a`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  
  &:before {
    content: '→';
    margin-right: 8px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #ff6e6e;
    padding-left: 5px;
  }
  
  &:hover:before {
    color: #ff6e6e;
  }
`;

// const NewsletterForm = styled.form`
//   margin-top: 20px;
// `;

// const FormGroup = styled.div`
//   display: flex;
//   margin-bottom: 15px;
// `;

// const FormInput = styled.input`
//   flex-grow: 1;
//   padding: 12px 10px;
//   border: none;
//   border-radius: 4px 0 0 4px;
//   font-size: 14px;
//   color: #333;
  
//   &:focus {
//     outline: none;
//   }
// `;

// const SubmitButton = styled.button`
//   background-color: #ff6e6e;
//   color: #fff;
//   border: none;
//   padding: 0 10px;
//   border-radius: 0 4px 4px 0;
//   font-size: 14px;
//   font-weight: 500;
//   cursor: pointer;
//   transition: all 0.3s ease;
  
//   &:hover {
//     background-color: #e04d0d;
//   }
// `;

const FooterBottom = styled.div`
  padding: 25px 0;
  text-align: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const Copyright = styled.p`
  margin: 0;
  
  a {
    color: #ff6e6e;
    text-decoration: none;
  }
`;

const Footer = () => {
  return (
    <FooterSection>
      <Container>
        <FooterTop>
          <FooterWidget
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="with-border"
          >
            <FooterLogo>
              Zeni<span>Tech</span>
            </FooterLogo>
            <FooterText>
              We are a technology solutions company specializing in website development, UI/UX design, cloud infrastructure, and SEO optimization that drive results for businesses worldwide.
            </FooterText>
            <SocialLinks>
              <SocialLink href="https://www.instagram.com/zenitechsol/">
                <FontAwesomeIcon icon={faInstagram} />
              </SocialLink>
              <SocialLink href="https://www.facebook.com/zenitechsol">
                <FontAwesomeIcon icon={faFacebookF} />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/company/zenitechsol/">
                <FontAwesomeIcon icon={faLinkedinIn} />
              </SocialLink>
            </SocialLinks>
          </FooterWidget>
          
          <FooterWidget
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="with-border"
          >
            <WidgetTitle>Quick Links</WidgetTitle>
            <QuickLinks>
              <QuickLink>
                <Link href="/">Home</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/about-us">About Us</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/contact-us">Contact Us</Link>
              </QuickLink>
            </QuickLinks>
          </FooterWidget>
          
          <FooterWidget
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <WidgetTitle>Our Services</WidgetTitle>
            <QuickLinks>
              <QuickLink>
                <Link href="/services/website-development">Website Development</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/software-development">Software Development</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/mobile-app-development">Mobile App Development</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/server-setup">Server Setup</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/server-maintenance">Server Maintenance</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/database-management">Database Management</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/ui-ux-design">UI/UX Design</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/seo-optimization">SEO Optimization</Link>
              </QuickLink>
              <QuickLink>
                <Link href="/services/cloud-support">Cloud Support</Link>
              </QuickLink>
            </QuickLinks>
          </FooterWidget>
          

          
          {/* <FooterWidget
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <WidgetTitle>Newsletter</WidgetTitle>
            <FooterText>
              Subscribe to our newsletter to receive the latest updates and news from our team.
            </FooterText>
            <NewsletterForm>
              <FormGroup>
                <FormInput type="email" placeholder="Your Email Address" required />
                <SubmitButton type="submit">Subscribe</SubmitButton>
              </FormGroup>
            </NewsletterForm>
          </FooterWidget> */}
        </FooterTop>
        
        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} <a href="/">ZeniTech Solutions</a>. All Rights Reserved.
          </Copyright>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer;